import { useEffect } from 'react';
import classNames from 'classnames';

import { I18n } from 'common/i18n';
import { ComponentType } from 'types';
import StorytellerUtils from 'lib/StorytellerUtils';
import { useRichTextEditor } from 'editor/hooks/useRichTextEditor';
import { useStoryLayout } from 'editor/hooks/useStoryLayout';
import { type ReactRendererProps } from '../shared/ComponentRegistry';
import { addNoopenerToBlankLinks } from './add-noopener-to-blank-links';

export const ComponentHtml = ({ componentData, theme, editMode }: ReactRendererProps) => {
  const { type, value } = componentData;
  const isTableOfContents = type === ComponentType.HTML_TABLE_OF_CONTENTS;
  const isEmptyTableOfContents = isTableOfContents && value === null;
  const typeClasses = StorytellerUtils.typeToClassesForComponentType(type);

  const layout = useStoryLayout();
  const { ref: editorRef } = useRichTextEditor({ content: value ?? '', theme, layout });

  // Squire fires a content-change event for the empty ToC placeholder even though there's no
  // real content (it treats blank content as a single <br>). Stop it on the way out of this
  // component, before it reaches the story-level listener that would persist it as real content.
  useEffect(() => {
    const element = editorRef.current;
    if (!element) {
      return;
    }

    const stopContentChange = (event: Event) => {
      if (editMode && isEmptyTableOfContents) {
        event.stopPropagation();
      }
    };

    element.addEventListener('rich-text-editor::content-change', stopContentChange);
    return () => element.removeEventListener('rich-text-editor::content-change', stopContentChange);
  }, [editMode, isEmptyTableOfContents, editorRef]);

  if (!editMode) {
    return (
      <div
        className={classNames('component', typeClasses, 'typeset squire-formatted')}
        dangerouslySetInnerHTML={{ __html: addNoopenerToBlankLinks(value ?? '') }}
      />
    );
  }

  /**
   * The editor mounts into this element rather than a child of it: RichTextEditor puts
   * `data-editor-id` on its container and appends its iframe there, and both SharedRenderingUtils
   * and the edit stylesheets expect those on the `.component` element itself.
   */
  return (
    <div
      ref={editorRef}
      data-testid="component-html-editor"
      className={classNames('component', typeClasses, {
        'component-html-table-of-contents-empty': isEmptyTableOfContents,
      })}
    >
      {isTableOfContents && (
        <div className="component-html-table-of-contents-placeholder">
          {I18n.t('editor.table_of_contents.placeholder')}
        </div>
      )}
      <div className="component-blinder hidden" />
    </div>
  );
};
